import type { SiteConfigData, SiteConfigFeature, SiteConfigShortcut } from '../types';

export const defaultFeatures: SiteConfigFeature[] = Array(8).fill(null).map(() => ({ title: '', description: '' }));

export const defaultHomeShortcuts: SiteConfigShortcut[] = [
    { id: 'guides', to: '/game', icon: 'ri-book-open-line', imageUrl: '' },
    { id: 'mounts', to: '/game/montarias', icon: 'ri-flight-takeoff-line', imageUrl: '' },
    { id: 'classes', to: '/classes', icon: 'ri-sword-line', imageUrl: '' },
    { id: 'rankings', to: '/rankings', icon: 'ri-trophy-line', imageUrl: '' },
];

export const DEFAULT_ORIGINAL_CONFIG: SiteConfigData = {
    links: {
        discord: 'https://discord.gg/ps9smnSu9j',
        supportTicket: 'https://discord.com/channels/1463913142328234223/1464036874992488603',
    },
    home: {
        heroSubtitle: 'RIDE THE SKIES',
        heroTitle: 'Epic Dragon',
        heroTitleHighlight: 'ADVENTURE',
        heroDescription: 'Soar through the skies on the back of legendary dragons. Choose your path, master your mount, and become a legend in Knight Icarus Online.',
        features: [
            { title: 'Tame & Ride Legendary Beasts', description: 'Capture wild familiars in the field, raise their loyalty, then contract them as flying mounts, ground mounts, or combat pets.' },
            { title: 'Combat on Mount Back', description: 'Fight while mounted: use mount skills in mid-air and on the ground. Aerial PvP and PvE where positioning and mount choice define the outcome of battles.' },
            { title: 'Hakanas & the World of Icarus', description: 'Start in the capital Hakanas, then explore zones like Ellora, Lephantis, and the outer realms.' },
            { title: '8 Classes: Assassin to Priest', description: 'Assassin, Archer, Mage, Berserker, Guardian, Trickster, Magician, Priest. Each has distinct skills and roles in party dungeons and PvP.' },
            { title: 'Instance Dungeons & Raids', description: 'Enter instanced dungeons with your party, clear bosses, and earn gear and materials.' },
            { title: 'Guilds & Guild Content', description: 'Create or join a guild for guild quests, guild wars, and shared goals.' },
            { title: 'Rankings & Season Rewards', description: 'Climb PvP and PvE rankings for seasonal rewards. Top Riders earn titles, cosmetics, and exclusive items.' },
            { title: 'Cash Shop & Character Customization', description: 'Customize your Rider with costumes, mount skins, and convenience items from the in-game cash shop.' },
        ],
        shortcuts: defaultHomeShortcuts.map(s => ({ ...s })),
    },
    theme: {
        goldPrimary: '#b8922e',
        goldHover: '#9a7a24',
        goldLight: '#e8d5a8',
        dragonBlue: '#2d6ba3',
        dragonBlueDark: '#1a4d7a',
        dragonBlueLight: '#4a8fd4',
        bgDark: '#f4f0e8',
        bgCard: '#ffffff',
        bgCardHover: '#faf8f4',
        textPrimary: '#1a2a3a',
        textSecondary: '#5a6573',
        textMuted: '#8a9299',
        goldGlow: '0 8px 32px rgba(184, 146, 46, 0.15)',
        borderGold: 'rgba(184, 146, 46, 0.35)',
        gradientStart: '#b8922e',
        gradientEnd: '#2d6ba3',
        buttonPrimaryColor: '#b8922e',
        buttonSecondaryColor: '#1a4d7a',
    },
    general: {
        siteName: 'Knight Icarus Online',
    },
    navbar: {
        logo: {
            src: '/assets/images/logo.png',
            alt: 'Knight Icarus Online',
            heightPx: 40,
        },
        logoPosition: 'left',
        menuItems: [
            { id: 'news', type: 'internal', href: '/news', label: { en: 'News', 'pt-BR': 'Notícias' }, visible: true, order: 1 },
            { id: 'game', type: 'internal', href: '/game', label: { en: 'Game Guide', 'pt-BR': 'Guia do jogo' }, visible: true, order: 2 },
            { id: 'classes', type: 'internal', href: '/classes', label: { en: 'Classes', 'pt-BR': 'Classes' }, visible: true, order: 3 },
            { id: 'rankings', type: 'internal', href: '/rankings', label: { en: 'Rankings', 'pt-BR': 'Rankings' }, visible: true, order: 4 },
            { id: 'discord', type: 'external', href: 'https://discord.gg/ps9smnSu9j', label: { en: 'Discord', 'pt-BR': 'Discord' }, visible: true, newTab: true, order: 5 },
            { id: 'download', type: 'internal', href: '/download', label: { en: 'Download', 'pt-BR': 'Download' }, visible: true, order: 6 },
            { id: 'live', type: 'internal', href: '/live', label: { en: 'Live', 'pt-BR': 'Lives' }, visible: true, order: 7 },
        ],
    },
};

export const THEME_FIELDS: { key: keyof NonNullable<SiteConfigData['theme']>; label: string; group: string; type?: 'color' | 'text' }[] = [
    { key: 'goldPrimary', label: 'Ouro principal', group: 'Primárias (ouro/destaque)' },
    { key: 'goldHover', label: 'Ouro hover', group: 'Primárias (ouro/destaque)' },
    { key: 'goldLight', label: 'Ouro claro', group: 'Primárias (ouro/destaque)' },
    { key: 'dragonBlue', label: 'Azul principal', group: 'Secundárias (azul)' },
    { key: 'dragonBlueDark', label: 'Azul escuro', group: 'Secundárias (azul)' },
    { key: 'dragonBlueLight', label: 'Azul claro', group: 'Secundárias (azul)' },
    { key: 'bgDark', label: 'Fundo escuro', group: 'Fundos' },
    { key: 'bgCard', label: 'Fundo card', group: 'Fundos' },
    { key: 'bgCardHover', label: 'Fundo card hover', group: 'Fundos' },
    { key: 'textPrimary', label: 'Texto principal', group: 'Textos' },
    { key: 'textSecondary', label: 'Texto secundário', group: 'Textos' },
    { key: 'textMuted', label: 'Texto suave', group: 'Textos' },
    { key: 'buttonPrimaryColor', label: 'Botão primário', group: 'Botões' },
    { key: 'buttonSecondaryColor', label: 'Botão secundário', group: 'Botões' },
    { key: 'borderGold', label: 'Borda dourada', group: 'Efeitos' },
    { key: 'goldGlow', label: 'Brilho (box-shadow)', group: 'Efeitos', type: 'text' },
    { key: 'gradientStart', label: 'Gradiente início', group: 'Efeitos' },
    { key: 'gradientEnd', label: 'Gradiente fim', group: 'Efeitos' },
];

export const COLOR_PALETTE: { name: string; colors: string[] }[] = [
    {
        name: 'Dourados e metálicos',
        colors: ['#d4af37', '#b8941f', '#f4e4bc', '#c9a227', '#ffd700', '#daa520', '#b8860b', '#996515', '#8b6914', '#f0e68c', '#eee8aa', '#daa520', '#c5b358', '#9c7c38', '#806517'],
    },
    {
        name: 'Azuis e frios',
        colors: ['#1a4d7a', '#0f2d47', '#2d6ba3', '#1e3a5f', '#0d2137', '#3d7ab5', '#1565c0', '#0d47a1', '#1976d2', '#42a5f5', '#2196f3', '#0d47a1', '#283593', '#1a237e', '#3949ab'],
    },
    {
        name: 'Vermelhos e quentes',
        colors: ['#b91c1c', '#dc2626', '#ef4444', '#f87171', '#991b1b', '#7f1d1d', '#450a0a', '#c2410c', '#ea580c', '#f97316', '#dc2626', '#b91c1c', '#9a3412', '#c2410c', '#ea580c'],
    },
    {
        name: 'Verdes e esmeralda',
        colors: ['#166534', '#15803d', '#22c55e', '#4ade80', '#14532d', '#052e16', '#166534', '#15803d', '#16a34a', '#22c55e', '#2ecc71', '#27ae60', '#1e7b4a', '#0d5c2e', '#0a3d21'],
    },
    {
        name: 'Roxos e violetas',
        colors: ['#581c87', '#7e22ce', '#a855f7', '#c084fc', '#3b0764', '#4c1d95', '#5b21b6', '#6d28d9', '#7c3aed', '#8b5cf6', '#9333ea', '#6b21a8', '#86198f', '#701a75', '#4a044e'],
    },
    {
        name: 'Neutros e cinzas',
        colors: ['#0a0a0a', '#141414', '#1a1a1a', '#262626', '#404040', '#525252', '#737373', '#a3a3a3', '#d4d4d4', '#fafafa', '#ffffff', '#171717', '#0f0f0f', '#2d2d2d', '#525252'],
    },
];

export type AdminSettingsSection = 'general' | 'links' | 'navbar' | 'home' | 'download' | 'theme' | 'guildRank';

export const SETTINGS_SECTIONS: { id: AdminSettingsSection; label: string; description: string }[] = [
    { id: 'general', label: 'Informações gerais', description: 'Nome do site e identidade básica.' },
    { id: 'links', label: 'Links e Discord', description: 'Convite do Discord e link de suporte.' },
    { id: 'navbar', label: 'Navbar e menu', description: 'Logo, posição e itens do menu.' },
    { id: 'home', label: 'Home e cards', description: 'Hero da página inicial, SEO e cards de features.' },
    { id: 'download', label: 'Página de download', description: 'Textos da página /download em EN e PT-BR.' },
    { id: 'theme', label: 'Cores e tema', description: 'Paleta de cores, efeitos e temas salvos.' },
    { id: 'guildRank', label: 'Cards de guild', description: 'Posição, tamanho e cor dos elementos do card de guild no ranking.' },
];
